import {
  addDays,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  min,
  parseISO,
} from 'date-fns';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { DataInterval } from 'lib_ts/enums/data.enums';
import {
  HitterDataSource,
  HitterRosterStatus,
} from 'lib_ts/enums/hitters.enums';
import { IHitter, IUsageSummary } from 'lib_ts/interfaces/i-hitter';
import { compact, sortBy, sumBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { HittersService } from 'services/hitters.service';
import useHittersStore from 'stores/use-hitters-store';

// this is fixed based on the length of the period
const getUsageGranularity = (start: Date, end: Date): DataInterval => {
  const diff = differenceInDays(end, start);

  if (diff < 7 * 5) {
    // use daily until 5 weeks
    return DataInterval.Daily;
  }

  if (diff < 30 * 5) {
    // use weekly until 5 months
    return DataInterval.Weekly;
  }

  return DataInterval.Monthly;
};

const getUsageIndex = (date: Date, start: Date, granularity: DataInterval) => {
  switch (granularity) {
    case DataInterval.Daily:
      return differenceInDays(date, start);
    case DataInterval.Weekly:
      return differenceInWeeks(date, start);
    case DataInterval.Monthly:
      return differenceInMonths(date, start);
    default:
      return 0;
  }
};

// reapply filters to the hitters in store, unset active hitter if filtered away, and re-fetch summaries based on summaryFilters
const useSyncHittersFilters = () => {
  const hitters = useHittersStore((s) => s.hitters);

  // a stable key based on membership of hitters (i.e. mixin values changing will not trigger a re-fetch)
  const hittersIdKey = useMemo(() => {
    return MiscHelper.hashCode(
      hitters
        .map((h) => h._id)
        .sort()
        .join('|')
    );
  }, [hitters]);

  const hitterFilters = useHittersStore((s) => s.hitterFilters);
  const filteredHitters = useHittersStore((s) => s.filteredHitters);

  const usageFilters = useHittersStore((s) => s.usageFilters);

  // MARK: when filters change, reapply them to the hitters in store
  useEffect(() => {
    const safeNameFilter = hitterFilters.name?.toLowerCase() ?? '';

    const nextFiltered = hitters
      .filter((h) => {
        if (!safeNameFilter) {
          return true;
        }

        return h.name.toLowerCase().includes(safeNameFilter);
      })
      .filter((h) => !hitterFilters.side || hitterFilters.side === h.side)
      .filter((h) => !hitterFilters.level || hitterFilters.level === h.level)
      .filter((h) => {
        if (!hitterFilters.status) {
          return true;
        }

        switch (hitterFilters.status) {
          case HitterRosterStatus.NA: {
            // no status => old hitter, treat the same as manual
            return !h.status || h.status === HitterRosterStatus.NA;
          }

          case HitterRosterStatus.OffRoster:
          case HitterRosterStatus.OnRoster:
          default: {
            return hitterFilters.status === h.status;
          }
        }
      })
      .filter((h) => {
        if (!hitterFilters.source) {
          return true;
        }

        switch (hitterFilters.source) {
          case HitterDataSource.Manual: {
            // no source => old hitter, treat the same as manual
            return !h.source || h.source === HitterDataSource.Manual;
          }

          case HitterDataSource.StatsApi:
          case HitterDataSource.Csv:
          default: {
            return hitterFilters.source === h.source;
          }
        }
      });

    // sort results by name
    nextFiltered.sort((a, b) => StringHelper.safeSort(a.name, b.name));

    useHittersStore.setState({ filteredHitters: nextFiltered });
  }, [
    hitterFilters.name,
    hitterFilters.side,
    hitterFilters.level,
    hitterFilters.status,
    hitterFilters.source,
    hitters,
  ]);

  // MARK: when filtered changes, if active hitter is no longer visible, unset it
  useEffect(() => {
    const latestActiveHitter = useHittersStore.getState().activeHitter;

    if (!latestActiveHitter) {
      // do nothing
      return;
    }

    if (filteredHitters.find((h) => h._id === latestActiveHitter._id)) {
      // still visible, do nothing
      return;
    }

    // unset active hitter
    useHittersStore.setState({ activeHitter: undefined });
  }, [filteredHitters]);

  // MARK: when summaryFilters change, re-fetch summaries and mixin into hitters
  useEffect(() => {
    let cancelled = false;

    HittersService.getInstance()
      .getUsageSummaries(usageFilters)
      .then((grouped) => {
        if (cancelled) {
          return;
        }

        useHittersStore.setState({ loading: true });

        const start = parseISO(usageFilters.currPeriod.start);
        const end = parseISO(usageFilters.currPeriod.end);

        // determine the granularity of the summaries based on start and end dates
        const granularity = getUsageGranularity(start, end);

        const lastIndex = getUsageIndex(end, start, granularity);

        const nextHitters = useHittersStore.getState().hitters.map((hitter) => {
          // there should only one per day
          const summaries = grouped[hitter._id];

          // ? b/c some hitters may have no summaries from the current period, this is more accurate re: anticipating undefined
          if (!summaries || summaries.length === 0) {
            const nextHitter: IHitter = {
              ...hitter,
              // fill in 0s for all data
              _mx_usageData: Array.from({ length: lastIndex + 1 }, (_, i) => ({
                i: i,
                v: 0,
                date: addDays(start, i),
              })),
              _mx_totalSessions: 0,
              _mx_totalShots: 0,
              _mx_avgShotsPerSession: 0,
            };

            return nextHitter;
          }

          // map the summaries to get index based on granularity
          const mapped = summaries.map((h) => {
            const date = parseISO(h.date);
            const index = getUsageIndex(date, start, granularity);

            const o: IUsageSummary = {
              date: date,
              i: index,
              v: h.ctSessions, // * change this if the charts need to tally something else
            };

            return o;
          });

          // group by index so that multiple days can collapse into one entry for less granular modes (e.g. everything from the same week)
          const groupedByIndex = ArrayHelper.groupByFn(mapped, (s) =>
            s.i.toString()
          );

          // console.debug(`${hitter.name}: groupedByIndex`, groupedByIndex);

          // tally up the sessions and shots
          const totalSessions = sumBy(summaries, 'ctSessions');
          const totalShots = sumBy(summaries, 'ctShots');

          // calculate the average shots per session for each hitter
          const avgShotsPerSession =
            totalSessions > 0 ? totalShots / totalSessions : 0; // avoid division by zero

          const sparseUsageData = sortBy(
            compact(
              Object.keys(groupedByIndex).map((index) => {
                const byIndex = groupedByIndex[index];

                if (!byIndex || byIndex.length === 0) {
                  return undefined;
                }

                const o: IUsageSummary = {
                  i: byIndex[0].i,
                  v: sumBy(byIndex, 'v'),
                  date: min(byIndex.map((s) => s.date)),
                };

                return o;
              })
            ),
            'i'
          );

          const denseUsageData = Array.from(
            { length: lastIndex + 1 },
            (_, i) => {
              const data = sparseUsageData.find((s) => s.i === i);

              if (!data) {
                // fill in 0s for missing data
                return {
                  i: i,
                  v: 0,
                  date: addDays(start, i),
                };
              }

              return data;
            }
          );

          const nextHitter: IHitter = {
            ...hitter,
            _mx_usageData: sortBy(denseUsageData, 'i'),
            _mx_totalSessions: totalSessions,
            _mx_totalShots: totalShots,
            _mx_avgShotsPerSession: avgShotsPerSession,
          };

          return nextHitter;
        });

        // update the hitters in store with the new _mx_usageData
        useHittersStore.setState({ hitters: nextHitters, loading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [hittersIdKey, usageFilters]);
};

export default useSyncHittersFilters;
